import { apiClient } from "@niutraan/hooks/src/lib/api";
import type {
  CreateRoleDto,
  RoleDto,
  UpdateRoleDto,
} from "@niutraan/shared-dto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Query keys
const QUERY_KEYS = {
  roles: "roles",
};

// Get all roles
export const useRoles = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.roles],
    queryFn: async () => {
      try {
        return await apiClient.get<RoleDto[]>("/admin/roles");
      } catch (error) {
        console.error("Error fetching roles:", error);
        throw error;
      }
    },
  });
};

// Get role by ID
export const useRoleById = (id: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.roles, id],
    queryFn: async () => {
      try {
        return await apiClient.get<RoleDto>(`/admin/roles/${id}`);
      } catch (error) {
        console.error(`Error fetching role with ID ${id}:`, error);
        throw error;
      }
    },
    enabled: !!id,
  });
};

// Create role
export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newRole: CreateRoleDto) =>
      apiClient.post<RoleDto>("/admin/roles", newRole),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.roles] });
    },
    onError: (error) => {
      console.error("Error creating role:", error);
    },
  });
};

// Update role
export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleDto }) =>
      apiClient.patch<RoleDto>(`/admin/roles/${id}`, data),
    onSuccess: (_, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.roles] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.roles, variables.id],
      });
    },
    onError: (error) => {
      console.error("Error updating role:", error);
    },
  });
};

// Delete role
export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // First check if role has users assigned
      try {
        const response = await apiClient.get<{ count: number }>(
          `/admin/roles/${id}/users/count`,
        );
        if (response.count > 0) {
          throw new Error(
            "This role has users assigned to it. Please remove all users from this role before deleting it.",
          );
        }
        return apiClient.delete<void>(`/admin/roles/${id}`);
      } catch (error) {
        console.error("Error in delete role process:", error);
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.roles] });
    },
    onError: (error) => {
      console.error("Error deleting role:", error);
    },
  });
};

// Check if a role has users assigned
export const useCheckRoleHasUsers = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const response = await apiClient.get<{ count: number }>(
          `/admin/roles/${id}/users/count`,
        );
        return { id, hasUsers: response.count > 0 };
      } catch (error) {
        console.error("Error checking if role has users:", error);
        // If there's an error, assume there are users to prevent accidental deletion
        return { id, hasUsers: true, error };
      }
    },
  });
};

// Assign permissions to role
export const useAssignPermissionsToRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: string[] }) =>
      apiClient.put<RoleDto>(`/admin/roles/${id}/permissions`, { permissions }),
    onSuccess: (_, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.roles] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.roles, variables.id],
      });
    },
    onError: (error) => {
      console.error("Error assigning permissions to role:", error);
    },
  });
};
